import { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, DollarSign, Building2, Users, Clock, Store, XCircle, CheckCircle, AlertTriangle, Edit2, History } from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';

export default function AdminUnitDetailView({ unitId, onBack }) {
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resalePreview, setResalePreview] = useState(null);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showMarketValueModal, setShowMarketValueModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Form states
  const [newMarketValue, setNewMarketValue] = useState('');
  const [marketValueReason, setMarketValueReason] = useState('');
  const [resaleTarget, setResaleTarget] = useState('');
  const [listingReason, setListingReason] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUnit();
  }, [unitId]);

  const fetchUnit = async () => {
    setError(null);
    try {
      const response = await api.get(`/units/${unitId}`);
      setUnit(response.data.unit);
      setNewMarketValue(response.data.unit?.valuation?.currentMarketValue || '');
      setResaleTarget(response.data.unit?.valuation?.currentMarketValue || '');
    } catch (err) {
      setError('Failed to load unit details');
      toast.error('Failed to load unit details');
    } finally {
      setLoading(false);
    }
  };

  const fetchResalePreview = async (target) => {
    if (!target || target <= 0) return;
    try {
      const response = await api.get(`/admin/units/${unitId}/resale-preview?resale_target=${target}`);
      setResalePreview(response.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  // Debounced preview fetch
  useEffect(() => {
    if (showListModal && resaleTarget) {
      const timer = setTimeout(() => fetchResalePreview(resaleTarget), 300);
      return () => clearTimeout(timer);
    }
  }, [resaleTarget, showListModal]);

  const formatINR = (val) => {
    if (!val && val !== 0) return '₹0';
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const handleUpdateMarketValue = async () => {
    if (!newMarketValue || !marketValueReason) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/units/${unitId}/market-value`, {
        currentMarketValue: parseFloat(newMarketValue),
        reason: marketValueReason
      });
      await fetchUnit();
      setShowMarketValueModal(false);
      setMarketValueReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update market value');
    } finally {
      setActionLoading(false);
    }
  };

  const handleListUnit = async () => {
    if (!resaleTarget) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/units/${unitId}/list-secondary`, {
        resaleTargetValue: parseFloat(resaleTarget),
        reason: listingReason || 'Listed via admin panel'
      });
      await fetchUnit();
      setShowListModal(false);
      setListingReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to list unit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdrawUnit = async () => {
    if (!withdrawReason) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/units/${unitId}/withdraw-secondary`, {
        reason: withdrawReason
      });
      await fetchUnit();
      setShowWithdrawModal(false);
      setWithdrawReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to withdraw unit');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4ADE80]/30 border-t-[#4ADE80] rounded-full animate-spin" />
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="p-10">
        <p className="text-gray-500">Unit not found</p>
      </div>
    );
  }

  const valuation = unit.valuation || {};
  const funding = unit.funding || {};
  const secondary = unit.secondary || {};
  const lockIn = unit.lockIn || {};
  const marketValueHistory = unit.marketValueHistory || [];

  return (
    <div className="p-10">
      {/* Header */}
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Property
      </button>

      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-3xl font-light text-white">{unit.unitName}</h1>
          </div>
          <p className="text-gray-500">{unit.projectName} · {unit.unitId}</p>
        </div>
        <span className={`px-4 py-2 text-sm border ${
          (unit.lifecycle?.currentState || unit.status) === 'FUNDED' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
          (unit.lifecycle?.currentState || unit.status) === 'OPEN' ? 'bg-[#4ADE80]/20 text-[#4ADE80] border-[#4ADE80]/30' :
          'bg-gray-500/20 text-gray-400 border-gray-500/30'
        }`}>
          {(unit.lifecycle?.currentState || unit.status || 'DRAFT').toUpperCase()}
        </span>
      </div>

      {/* Valuation Section */}
      <div className="border border-white/10 p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#4ADE80]" />
            Valuation
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHistoryModal(true)}
              className="px-3 py-1.5 text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/20 flex items-center gap-1"
            >
              <History className="w-3 h-3" />
              History
            </button>
            <button
              onClick={() => {
                setNewMarketValue(valuation.currentMarketValue || valuation.baseValue || '');
                setShowMarketValueModal(true);
              }}
              className="px-3 py-1.5 text-xs border border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/10 flex items-center gap-1"
              data-testid="edit-market-value-btn"
            >
              <Edit2 className="w-3 h-3" />
              Edit Market Value
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Base Value (Immutable)</p>
            <p className="text-xl font-mono text-white">{formatINR(valuation.baseValue)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Current Market Value</p>
            <p className="text-xl font-mono text-[#4ADE80]">{formatINR(valuation.currentMarketValue)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Appreciation</p>
            <p className={`text-xl font-mono ${valuation.appreciationPercent >= 0 ? 'text-[#4ADE80]' : 'text-red-400'}`}>
              {valuation.appreciationPercent >= 0 ? '+' : ''}{valuation.appreciationPercent?.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Appreciation Amount</p>
            <p className={`text-xl font-mono ${valuation.appreciationAmount >= 0 ? 'text-[#4ADE80]' : 'text-red-400'}`}>
              {valuation.appreciationAmount >= 0 ? '+' : ''}{formatINR(valuation.appreciationAmount)}
            </p>
          </div>
        </div>
      </div>

      {/* Funding Section */}
      <div className="border border-white/10 p-6 mb-6">
        <h2 className="text-lg text-white flex items-center gap-2 mb-6">
          <TrendingUp className="w-5 h-5 text-[#4ADE80]" />
          Funding Status
        </h2>

        <div className="grid grid-cols-4 gap-6 mb-6">
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Total Deployed</p>
            <p className="text-xl font-mono text-white">{formatINR(funding.totalDeployed)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Tranche Progress</p>
            <p className="text-xl font-mono text-[#4ADE80]">{funding.fundedPercent?.toFixed(0)}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Overall Progress</p>
            <p className="text-xl font-mono text-white">{funding.overallFundedPercent?.toFixed(0)}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Investors</p>
            <p className="text-xl font-mono text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-500" />
              {funding.investorCount || 0}
            </p>
          </div>
        </div>

        {funding.currentTranche && (
          <div className="bg-white/5 p-4 border border-white/10">
            <p className="text-xs text-gray-600 uppercase mb-2">Current Tranche</p>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white font-medium">{funding.currentTranche.id}: {funding.currentTranche.name}</p>
                <p className="text-sm text-gray-500">Target: {formatINR(funding.currentTranche.target)}</p>
              </div>
              <div className="text-right">
                <p className="text-[#4ADE80] font-mono">{formatINR(funding.currentTranche.raised)} raised</p>
                <p className="text-sm text-gray-500">{funding.currentTranche.progress?.toFixed(0)}% complete</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lock-In Section */}
      <div className="border border-white/10 p-6 mb-6">
        <h2 className="text-lg text-white flex items-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-[#4ADE80]" />
          Lock-In Status
        </h2>

        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Status</p>
            <p className={`text-lg font-medium ${lockIn.isLocked ? 'text-yellow-400' : 'text-[#4ADE80]'}`}>
              {lockIn.isLocked ? '🔒 Locked' : '🔓 Unlocked'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Lock-In Period</p>
            <p className="text-lg font-mono text-white">{lockIn.periodMonths || 18} months</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 uppercase mb-2">Days Remaining</p>
            <p className="text-lg font-mono text-white">{lockIn.daysRemaining || '—'}</p>
          </div>
        </div>
      </div>

      {/* Secondary Market Section */}
      <div className="border border-white/10 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg text-white flex items-center gap-2">
            <Store className="w-5 h-5 text-[#4ADE80]" />
            Secondary Market
          </h2>
          <span className={`px-3 py-1 text-xs border ${
            secondary.isListed ? 'bg-[#4ADE80]/20 text-[#4ADE80] border-[#4ADE80]/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          }`}>
            {secondary.isListed ? 'LISTED' : 'NOT LISTED'}
          </span>
        </div>

        {unit.status !== 'funded' ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 text-center">
            <AlertTriangle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-yellow-400">Unit must be in &quot;funded&quot; status to list on secondary market</p>
            <p className="text-sm text-gray-500 mt-1">Current status: {unit.status}</p>
          </div>
        ) : secondary.isListed ? (
          <div>
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <p className="text-xs text-gray-600 uppercase mb-2">Listing ID</p>
                <p className="text-white font-mono">{secondary.listingId}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase mb-2">Transferable</p>
                <p className={secondary.isTransferable ? 'text-[#4ADE80]' : 'text-yellow-400'}>
                  {secondary.isTransferable ? '✓ Yes' : '✗ Lock-in active'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase mb-2">Status</p>
                <p className="text-white">Active</p>
              </div>
            </div>
            <button
              onClick={() => setShowWithdrawModal(true)}
              className="w-full py-3 border border-red-500/50 text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-2"
              data-testid="withdraw-listing-btn"
            >
              <XCircle className="w-4 h-4" />
              Withdraw from Secondary Market
            </button>
          </div>
        ) : (
          <div>
            <div className="bg-white/5 p-4 border border-white/10 mb-4">
              <p className="text-sm text-gray-400">
                This unit is eligible for secondary market listing. You can set a resale target value which may differ from the current market value.
              </p>
            </div>
            <button
              onClick={() => {
                setResaleTarget(valuation.currentMarketValue || valuation.baseValue || '');
                setShowListModal(true);
              }}
              className="w-full py-3 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold flex items-center justify-center gap-2"
              data-testid="list-secondary-btn"
            >
              <Store className="w-4 h-4" />
              List on Secondary Market
            </button>
          </div>
        )}
      </div>

      {/* Market Value Edit Modal */}
      {showMarketValueModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl text-white">Update Market Value</h3>
              <p className="text-sm text-gray-500 mt-1">This change will be recorded in the audit trail</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">Current Value</label>
                <p className="text-lg font-mono text-gray-400">{formatINR(valuation.currentMarketValue)}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">New Market Value (₹)</label>
                <input
                  type="number"
                  value={newMarketValue}
                  onChange={(e) => setNewMarketValue(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#4ADE80]/50"
                  placeholder="Enter new value"
                  data-testid="new-market-value-input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">Reason for Change *</label>
                <input
                  type="text"
                  value={marketValueReason}
                  onChange={(e) => setMarketValueReason(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#4ADE80]/50"
                  placeholder="e.g., Q1 market appreciation"
                  data-testid="market-value-reason-input"
                />
              </div>
              {newMarketValue && valuation.baseValue && (
                <div className="bg-white/5 p-3 border border-white/10">
                  <p className="text-sm text-gray-400">
                    New appreciation: <span className={parseFloat(newMarketValue) >= valuation.baseValue ? 'text-[#4ADE80]' : 'text-red-400'}>
                      {(((parseFloat(newMarketValue) - valuation.baseValue) / valuation.baseValue) * 100).toFixed(1)}%
                    </span>
                  </p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => setShowMarketValueModal(false)}
                className="flex-1 py-3 border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMarketValue}
                disabled={!newMarketValue || !marketValueReason || actionLoading}
                className="flex-1 py-3 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-market-value-btn"
              >
                {actionLoading ? 'Saving...' : 'Update Value'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List on Secondary Modal */}
      {showListModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl text-white">List Unit for Resale</h3>
              <p className="text-sm text-gray-500 mt-1">Set the minimum acceptable offer price</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 uppercase mb-2">Base Value</label>
                  <p className="text-lg font-mono text-gray-400">{formatINR(valuation.baseValue)}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 uppercase mb-2">Current Market Value</label>
                  <p className="text-lg font-mono text-white">{formatINR(valuation.currentMarketValue)}</p>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">Resale Target Value (₹)</label>
                <input
                  type="number"
                  value={resaleTarget}
                  onChange={(e) => setResaleTarget(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#4ADE80]/50"
                  placeholder="Enter target resale price"
                  data-testid="resale-target-input"
                />
              </div>

              {resalePreview && (
                <div className={`p-4 border ${
                  resalePreview.isAtLoss ? 'bg-red-500/10 border-red-500/30' :
                  resalePreview.requiresVoting ? 'bg-yellow-500/10 border-yellow-500/30' :
                  'bg-[#4ADE80]/10 border-[#4ADE80]/30'
                }`}>
                  <div className="flex items-start gap-3">
                    {resalePreview.isAtLoss ? (
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    ) : resalePreview.requiresVoting ? (
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-[#4ADE80] flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${
                        resalePreview.isAtLoss ? 'text-red-400' :
                        resalePreview.requiresVoting ? 'text-yellow-400' :
                        'text-[#4ADE80]'
                      }`}>
                        {resalePreview.isAtLoss ? '⚠️ LISTING AT LOSS' :
                         resalePreview.requiresVoting ? '⚠️ REQUIRES INVESTOR VOTING' :
                         '✅ AUTO-QUALIFIES'}
                      </p>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-gray-400">
                          Appreciation: <span className={resalePreview.appreciationPercent >= 0 ? 'text-white' : 'text-red-400'}>
                            {resalePreview.appreciationPercent >= 0 ? '+' : ''}{resalePreview.appreciationPercent?.toFixed(1)}%
                          </span> ({formatINR(resalePreview.appreciationAmount)})
                        </p>
                        <p className="text-gray-400">
                          Avg holding period: <span className="text-white">{resalePreview.avgYearsHeld?.toFixed(2)} years</span>
                        </p>
                        <p className="text-gray-400">
                          Appreciation rate: <span className={resalePreview.appreciationRatePA >= 5 ? 'text-[#4ADE80]' : 'text-yellow-400'}>
                            {resalePreview.appreciationRatePA?.toFixed(1)}% p.a.
                          </span>
                          <span className="text-gray-600"> (threshold: 5% p.a.)</span>
                        </p>
                      </div>
                      {resalePreview.requiresVoting && !resalePreview.isAtLoss && (
                        <p className="mt-2 text-xs text-yellow-400/80">
                          Offers below this threshold will require investor approval via governance vote.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">Listing Reason (Optional)</label>
                <input
                  type="text"
                  value={listingReason}
                  onChange={(e) => setListingReason(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#4ADE80]/50"
                  placeholder="e.g., T1 complete, ready for secondary"
                />
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  setShowListModal(false);
                  setResalePreview(null);
                }}
                className="flex-1 py-3 border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleListUnit}
                disabled={!resaleTarget || actionLoading}
                className="flex-1 py-3 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-list-btn"
              >
                {actionLoading ? 'Listing...' : 'List Unit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl text-white">Withdraw from Secondary Market</h3>
              <p className="text-sm text-gray-500 mt-1">This will cancel the active listing</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
                <p className="text-yellow-400 text-sm">
                  Withdrawing will remove this unit from the secondary marketplace. Any pending offers will be cancelled.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 uppercase mb-2">Reason for Withdrawal *</label>
                <input
                  type="text"
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#4ADE80]/50"
                  placeholder="e.g., Re-evaluating pricing strategy"
                  data-testid="withdraw-reason-input"
                />
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="flex-1 py-3 border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawUnit}
                disabled={!withdrawReason || actionLoading}
                className="flex-1 py-3 bg-red-500 hover:bg-red-500/90 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-withdraw-btn"
              >
                {actionLoading ? 'Withdrawing...' : 'Withdraw Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl text-white">Market Value History</h3>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-500 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {marketValueHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No market value changes recorded yet</p>
              ) : (
                <div className="space-y-4">
                  {[...marketValueHistory].reverse().map((entry, idx) => (
                    <div key={idx} className="border border-white/10 p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-mono text-white">{formatINR(entry.value)}</p>
                        <span className={`text-sm ${entry.value > (entry.previousValue || 0) ? 'text-[#4ADE80]' : 'text-red-400'}`}>
                          {entry.previousValue ? (entry.value > entry.previousValue ? '↑' : '↓') : '—'} from {formatINR(entry.previousValue)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{entry.reason}</p>
                      <div className="flex justify-between mt-2 text-xs text-gray-600">
                        <span>By: {entry.updatedBy}</span>
                        <span>{new Date(entry.updatedAt).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
